import { jsPDF } from 'jspdf';

// Paper size dimensions in inches
const PAPER_SIZES = {
  letter: { width: 8.5, height: 11 },
  legal: { width: 8.5, height: 14 },
  tabloid: { width: 11, height: 17 },
  a4: { width: 8.27, height: 11.69 },
  a3: { width: 11.69, height: 16.54 },
};

// Common architectural scales
const SCALES = {
  fit: null, // Fit to page
  '1/8': 1/8,   // 1/8" = 1'
  '1/4': 1/4,   // 1/4" = 1'
  '3/8': 3/8,   // 3/8" = 1'
  '1/2': 1/2,   // 1/2" = 1'
  '3/4': 3/4,   // 3/4" = 1'
  '1': 1,       // 1" = 1'
  '1.5': 1.5,   // 1-1/2" = 1'
};

/**
 * Export the floor plan canvas to PDF
 */
export async function exportToPDF(canvas, options = {}) {
  const {
    paperSize = 'letter',
    orientation = 'landscape',
    scale = 'fit',
    margin = 0.5, // inches
    showHeader = true,
    showFooter = true,
    projectName = 'Floor Plan',
    companyName = '',
    showDate = true,
    showPageNumbers = true,
  } = options;

  // Get paper dimensions
  let paperDims = PAPER_SIZES[paperSize] || PAPER_SIZES.letter;

  // Swap dimensions for landscape
  if (orientation === 'landscape') {
    paperDims = { width: paperDims.height, height: paperDims.width };
  }

  // Create PDF document
  const pdf = new jsPDF({
    orientation: orientation,
    unit: 'in',
    format: [paperDims.width, paperDims.height],
  });

  // Calculate printable area
  const printableWidth = paperDims.width - (margin * 2);
  const printableHeight = paperDims.height - (margin * 2);

  // Reserve space for header and footer
  const headerHeight = showHeader ? 0.5 : 0;
  const footerHeight = showFooter ? 0.4 : 0;
  const contentHeight = printableHeight - headerHeight - footerHeight;
  const contentY = margin + headerHeight;

  // Get canvas image data
  const imgData = canvas.toDataURL('image/png', 1.0);

  // Calculate image dimensions
  const canvasAspect = canvas.width / canvas.height;
  const contentAspect = printableWidth / contentHeight;

  let imgWidth, imgHeight;

  if (scale === 'fit' || !SCALES[scale]) {
    // Fit to page
    if (canvasAspect > contentAspect) {
      // Canvas is wider - fit to width
      imgWidth = printableWidth;
      imgHeight = printableWidth / canvasAspect;
    } else {
      // Canvas is taller - fit to height
      imgHeight = contentHeight;
      imgWidth = contentHeight * canvasAspect;
    }
  } else {
    // Use specific scale (inches per foot on paper)
    // This requires knowing the actual floor plan dimensions
    // For now, we'll use a proportion of the canvas
    const scaleValue = SCALES[scale];
    imgWidth = printableWidth * Math.min(1, scaleValue);
    imgHeight = imgWidth / canvasAspect;

    // If it exceeds content area, scale down
    if (imgHeight > contentHeight) {
      imgHeight = contentHeight;
      imgWidth = contentHeight * canvasAspect;
    }
  }

  // Center the image
  const imgX = margin + (printableWidth - imgWidth) / 2;
  const imgY = contentY + (contentHeight - imgHeight) / 2;

  // Add header
  if (showHeader) {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(projectName, margin, margin + 0.25);

    if (companyName) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(companyName, margin, margin + 0.4);
    }

    // Draw header line
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.01);
    pdf.line(margin, margin + headerHeight - 0.05, paperDims.width - margin, margin + headerHeight - 0.05);
  }

  // Add the floor plan image
  pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth, imgHeight);

  // Add footer
  if (showFooter) {
    const footerY = paperDims.height - margin;

    // Draw footer line
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.01);
    pdf.line(margin, footerY - footerHeight + 0.05, paperDims.width - margin, footerY - footerHeight + 0.05);

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);

    // Date on left
    if (showDate) {
      const dateStr = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      pdf.text(dateStr, margin, footerY - 0.1);
    }

    // Page number on right
    if (showPageNumbers) {
      const pageText = `Page 1 of 1`;
      const textWidth = pdf.getTextWidth(pageText);
      pdf.text(pageText, paperDims.width - margin - textWidth, footerY - 0.1);
    }

    // "Created with FloorPlan Pro" in center
    const creditText = 'Created with FloorPlan Pro';
    const creditWidth = pdf.getTextWidth(creditText);
    pdf.text(creditText, (paperDims.width - creditWidth) / 2, footerY - 0.1);
  }

  // Generate filename
  const filename = `${projectName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;

  // Save the PDF
  pdf.save(filename);

  return filename;
}

/**
 * Export the floor plan canvas to PNG image
 */
export function exportToPNG(canvas, options = {}) {
  const {
    projectName = 'Floor Plan',
    scale = 1, // 1 = original, 2 = 2x, etc.
  } = options;

  // Create a scaled canvas if needed
  let exportCanvas = canvas;

  if (scale !== 1) {
    exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvas.width * scale;
    exportCanvas.height = canvas.height * scale;
    const ctx = exportCanvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.drawImage(canvas, 0, 0);
  }

  // Get image data
  const imgData = exportCanvas.toDataURL('image/png', 1.0);

  // Create download link
  const link = document.createElement('a');
  link.download = `${projectName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().slice(0, 10)}.png`;
  link.href = imgData;
  link.click();

  return link.download;
}

/**
 * Export the floor plan canvas to JPEG image
 */
export function exportToJPEG(canvas, options = {}) {
  const {
    projectName = 'Floor Plan',
    quality = 0.92,
    backgroundColor = '#ffffff',
  } = options;

  // Create a new canvas with white background
  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = canvas.width;
  exportCanvas.height = canvas.height;
  const ctx = exportCanvas.getContext('2d');

  // Fill with background color
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

  // Draw the original canvas
  ctx.drawImage(canvas, 0, 0);

  // Get image data
  const imgData = exportCanvas.toDataURL('image/jpeg', quality);

  // Create download link
  const link = document.createElement('a');
  link.download = `${projectName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().slice(0, 10)}.jpg`;
  link.href = imgData;
  link.click();

  return link.download;
}

/**
 * Export an estimate to professional PDF document
 */
export async function exportEstimateToPDF(estimateData, options = {}) {
  const {
    companyProfile = null,
    accentColor = [0, 100, 180], // RGB blue accent
  } = options;

  const {
    name: estimateName = 'Estimate',
    clientName = '',
    clientEmail = '',
    clientPhone = '',
    clientAddress = '',
    estimateDate = '',
    validUntil = '',
    lineItems = [],
    laborItems = [],
    materialsSubtotal = 0,
    laborSubtotal = 0,
    subtotal = 0,
    taxRate = 0,
    taxAmount = 0,
    total = 0,
    scopeOfWork = '',
    termsAndConditions = '',
  } = estimateData;

  // Create PDF document (Letter size, portrait)
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'in',
    format: 'letter',
  });

  const pageWidth = 8.5;
  const pageHeight = 11;
  const margin = 0.6;
  const contentWidth = pageWidth - (margin * 2);

  let y = margin;

  // Helper to format currency
  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;

  // Helper to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Helper to check if we need a new page
  const checkPageBreak = (neededHeight) => {
    if (y + neededHeight > pageHeight - margin - 0.5) {
      pdf.addPage();
      y = margin;
      return true;
    }
    return false;
  };

  // ============ HEADER SECTION ============

  // Company info (top right)
  if (companyProfile) {
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);

    const companyX = pageWidth - margin;
    let companyY = y;

    if (companyProfile.companyName) {
      const nameWidth = pdf.getTextWidth(companyProfile.companyName);
      pdf.text(companyProfile.companyName, companyX - nameWidth, companyY);
      companyY += 0.2;
    }

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(80, 80, 80);

    const companyLines = [];
    if (companyProfile.address) {
      companyProfile.address.split('\n').forEach(line => companyLines.push(line.trim()));
    }
    if (companyProfile.phone) companyLines.push(companyProfile.phone);
    if (companyProfile.email) companyLines.push(companyProfile.email);
    if (companyProfile.website) companyLines.push(companyProfile.website);
    if (companyProfile.licenseNumber) companyLines.push(companyProfile.licenseNumber);

    companyLines.forEach(line => {
      const lineWidth = pdf.getTextWidth(line);
      pdf.text(line, companyX - lineWidth, companyY);
      companyY += 0.16;
    });
  }

  // "ESTIMATE" title (top left, large)
  pdf.setFontSize(32);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
  pdf.text('ESTIMATE', margin, y + 0.3);

  y += 0.5;

  // Estimate date and valid until
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(80, 80, 80);

  if (estimateDate) {
    pdf.text(`Date: ${formatDate(estimateDate)}`, margin, y);
    y += 0.18;
  }
  if (validUntil) {
    pdf.text(`Valid Until: ${formatDate(validUntil)}`, margin, y);
    y += 0.18;
  }

  y += 0.3;

  // ============ CLIENT SECTION ============

  if (clientName) {
    // Draw client box
    pdf.setFillColor(248, 249, 250);
    pdf.roundedRect(margin, y, contentWidth, 0.8, 0.05, 0.05, 'F');

    y += 0.15;

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text('PREPARED FOR', margin + 0.15, y + 0.08);

    y += 0.2;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);
    pdf.text(clientName, margin + 0.15, y + 0.05);

    y += 0.2;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(80, 80, 80);

    const clientDetails = [];
    if (clientAddress) clientAddress.split('\n').forEach(line => clientDetails.push(line.trim()));
    if (clientPhone) clientDetails.push(clientPhone);
    if (clientEmail) clientDetails.push(clientEmail);

    clientDetails.slice(0, 3).forEach(line => {
      pdf.text(line, margin + 0.15, y + 0.05);
      y += 0.15;
    });

    y += 0.15;
  }

  y += 0.2;

  // ============ PROJECT NAME ============

  if (estimateName) {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);
    pdf.text(estimateName, margin, y);
    y += 0.35;
  }

  // ============ MATERIALS TABLE ============

  if (lineItems.length > 0) {
    checkPageBreak(1);

    // Section header
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.text('MATERIALS', margin, y);

    // Underline
    pdf.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.setLineWidth(0.02);
    pdf.line(margin, y + 0.05, margin + 1.2, y + 0.05);

    y += 0.25;

    // Table header
    const colWidths = {
      item: contentWidth * 0.45,
      qty: contentWidth * 0.12,
      unit: contentWidth * 0.13,
      price: contentWidth * 0.15,
      total: contentWidth * 0.15,
    };

    pdf.setFillColor(30, 35, 50);
    pdf.rect(margin, y, contentWidth, 0.28, 'F');

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);

    let colX = margin + 0.1;
    pdf.text('ITEM', colX, y + 0.18);
    colX += colWidths.item;
    pdf.text('QTY', colX, y + 0.18);
    colX += colWidths.qty;
    pdf.text('UNIT', colX, y + 0.18);
    colX += colWidths.unit;
    pdf.text('PRICE', colX, y + 0.18);
    colX += colWidths.price;
    pdf.text('TOTAL', colX, y + 0.18);

    y += 0.28;

    // Table rows
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(50, 50, 50);

    lineItems.forEach((item, index) => {
      checkPageBreak(0.35);

      // Alternate row background
      if (index % 2 === 0) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(margin, y, contentWidth, 0.3, 'F');
      }

      pdf.setFontSize(9);
      colX = margin + 0.1;

      // Item name (truncate if too long)
      let itemName = item.name || '';
      if (pdf.getTextWidth(itemName) > colWidths.item - 0.2) {
        while (pdf.getTextWidth(itemName + '...') > colWidths.item - 0.2 && itemName.length > 0) {
          itemName = itemName.slice(0, -1);
        }
        itemName += '...';
      }
      pdf.text(itemName, colX, y + 0.18);

      colX += colWidths.item;
      pdf.text(String(item.quantity || 0), colX, y + 0.18);

      colX += colWidths.qty;
      pdf.text(item.unit || '', colX, y + 0.18);

      colX += colWidths.unit;
      pdf.text(formatCurrency(item.unitPrice), colX, y + 0.18);

      colX += colWidths.price;
      const lineTotal = (item.quantity || 0) * (item.unitPrice || 0);
      pdf.text(formatCurrency(lineTotal), colX, y + 0.18);

      y += 0.3;
    });

    // Materials subtotal
    pdf.setFillColor(240, 248, 255);
    pdf.rect(margin, y, contentWidth, 0.28, 'F');

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.text('Materials Subtotal', margin + 0.1, y + 0.18);
    pdf.text(formatCurrency(materialsSubtotal), margin + contentWidth - 0.6, y + 0.18);

    y += 0.45;
  }

  // ============ LABOR TABLE ============

  if (laborItems.length > 0) {
    checkPageBreak(1);

    // Section header
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.text('LABOR', margin, y);

    // Underline
    pdf.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.setLineWidth(0.02);
    pdf.line(margin, y + 0.05, margin + 0.8, y + 0.05);

    y += 0.25;

    // Table header
    pdf.setFillColor(30, 35, 50);
    pdf.rect(margin, y, contentWidth, 0.28, 'F');

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text('DESCRIPTION', margin + 0.1, y + 0.18);
    pdf.text('AMOUNT', margin + contentWidth - 0.6, y + 0.18);

    y += 0.28;

    // Table rows
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(50, 50, 50);

    laborItems.forEach((item, index) => {
      checkPageBreak(0.35);

      if (index % 2 === 0) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(margin, y, contentWidth, 0.3, 'F');
      }

      pdf.setFontSize(9);
      pdf.text(item.description || '', margin + 0.1, y + 0.18);
      pdf.text(formatCurrency(item.amount), margin + contentWidth - 0.6, y + 0.18);

      y += 0.3;
    });

    // Labor subtotal
    pdf.setFillColor(240, 248, 255);
    pdf.rect(margin, y, contentWidth, 0.28, 'F');

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.text('Labor Subtotal', margin + 0.1, y + 0.18);
    pdf.text(formatCurrency(laborSubtotal), margin + contentWidth - 0.6, y + 0.18);

    y += 0.45;
  }

  // ============ TOTALS SECTION ============

  checkPageBreak(1);

  const totalsX = margin + contentWidth - 2.5;
  const totalsWidth = 2.5;

  // Subtotal
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 60, 60);
  pdf.text('Subtotal', totalsX, y + 0.15);
  pdf.text(formatCurrency(subtotal), totalsX + totalsWidth - 0.1, y + 0.15, { align: 'right' });
  y += 0.25;

  // Tax (if applicable)
  if (taxRate > 0) {
    pdf.text(`Tax (${taxRate}%)`, totalsX, y + 0.15);
    pdf.text(formatCurrency(taxAmount), totalsX + totalsWidth - 0.1, y + 0.15, { align: 'right' });
    y += 0.25;
  }

  // Grand total box
  pdf.setFillColor(30, 35, 50);
  pdf.rect(totalsX - 0.1, y, totalsWidth + 0.2, 0.4, 'F');

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('TOTAL', totalsX + 0.05, y + 0.26);
  pdf.text(formatCurrency(total), totalsX + totalsWidth - 0.05, y + 0.26, { align: 'right' });

  y += 0.7;

  // ============ SCOPE OF WORK ============

  if (scopeOfWork) {
    checkPageBreak(1);

    pdf.setFillColor(248, 249, 250);

    // Calculate height needed for scope text
    pdf.setFontSize(9);
    const scopeLines = pdf.splitTextToSize(scopeOfWork, contentWidth - 0.3);
    const scopeHeight = Math.max(0.8, scopeLines.length * 0.16 + 0.4);

    pdf.roundedRect(margin, y, contentWidth, scopeHeight, 0.05, 0.05, 'F');

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(100, 100, 100);
    pdf.text('SCOPE OF WORK', margin + 0.15, y + 0.2);

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);
    pdf.text(scopeLines, margin + 0.15, y + 0.4);

    y += scopeHeight + 0.2;
  }

  // ============ TERMS AND CONDITIONS ============

  if (termsAndConditions) {
    checkPageBreak(1);

    pdf.setFillColor(248, 249, 250);

    pdf.setFontSize(9);
    const termsLines = pdf.splitTextToSize(termsAndConditions, contentWidth - 0.3);
    const termsHeight = Math.max(0.8, termsLines.length * 0.16 + 0.4);

    pdf.roundedRect(margin, y, contentWidth, termsHeight, 0.05, 0.05, 'F');

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(100, 100, 100);
    pdf.text('TERMS & CONDITIONS', margin + 0.15, y + 0.2);

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);
    pdf.text(termsLines, margin + 0.15, y + 0.4);

    y += termsHeight + 0.2;
  }

  // ============ FOOTER ============

  // Draw footer at bottom of page
  const footerY = pageHeight - margin - 0.1;

  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.005);
  pdf.line(margin, footerY - 0.2, pageWidth - margin, footerY - 0.2);

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(120, 120, 120);

  // Thank you message
  pdf.text('Thank you for considering our services.', margin, footerY);

  // Company contact in footer (if available)
  if (companyProfile?.phone || companyProfile?.email) {
    const contactParts = [];
    if (companyProfile.phone) contactParts.push(companyProfile.phone);
    if (companyProfile.email) contactParts.push(companyProfile.email);
    const contactText = contactParts.join(' | ');
    const contactWidth = pdf.getTextWidth(contactText);
    pdf.text(contactText, pageWidth - margin - contactWidth, footerY);
  }

  // Generate filename
  const safeName = (estimateName || 'Estimate').replace(/[^a-z0-9]/gi, '_');
  const dateStr = new Date().toISOString().slice(0, 10);
  const filename = `${safeName}_${dateStr}.pdf`;

  // Save the PDF
  pdf.save(filename);

  return filename;
}

/**
 * Export an invoice to professional PDF document
 */
export async function exportInvoiceToPDF(invoiceData, options = {}) {
  const {
    companyProfile = null,
    accentColor = [0, 102, 204], // RGB blue accent for invoices
  } = options;

  const {
    invoiceNumber = '',
    name: invoiceName = 'Invoice',
    clientName = '',
    clientEmail = '',
    clientPhone = '',
    clientAddress = '',
    invoiceDate = '',
    dueDate = '',
    paymentTerms = 'net_30',
    customPaymentTerms = '',
    lineItems = [],
    laborItems = [],
    materialsSubtotal = 0,
    laborSubtotal = 0,
    subtotal = 0,
    taxRate = 0,
    taxAmount = 0,
    total = 0,
    scopeOfWork = '',
    termsAndConditions = '',
  } = invoiceData;

  // Payment terms display
  const PAYMENT_TERMS_LABELS = {
    due_on_receipt: 'Due on Receipt',
    net_15: 'Net 15',
    net_30: 'Net 30',
    net_45: 'Net 45',
    net_60: 'Net 60',
    custom: customPaymentTerms || 'Custom',
  };

  // Create PDF document (Letter size, portrait)
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'in',
    format: 'letter',
  });

  const pageWidth = 8.5;
  const pageHeight = 11;
  const margin = 0.6;
  const contentWidth = pageWidth - (margin * 2);

  let y = margin;

  // Helper to format currency
  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;

  // Helper to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Helper to check if we need a new page
  const checkPageBreak = (neededHeight) => {
    if (y + neededHeight > pageHeight - margin - 0.5) {
      pdf.addPage();
      y = margin;
      return true;
    }
    return false;
  };

  // ============ HEADER SECTION ============

  // Company info (top right)
  if (companyProfile) {
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);

    const companyX = pageWidth - margin;
    let companyY = y;

    if (companyProfile.companyName) {
      const nameWidth = pdf.getTextWidth(companyProfile.companyName);
      pdf.text(companyProfile.companyName, companyX - nameWidth, companyY);
      companyY += 0.2;
    }

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(80, 80, 80);

    const companyLines = [];
    if (companyProfile.address) {
      companyProfile.address.split('\n').forEach(line => companyLines.push(line.trim()));
    }
    if (companyProfile.phone) companyLines.push(companyProfile.phone);
    if (companyProfile.email) companyLines.push(companyProfile.email);
    if (companyProfile.website) companyLines.push(companyProfile.website);
    if (companyProfile.licenseNumber) companyLines.push(companyProfile.licenseNumber);

    companyLines.forEach(line => {
      const lineWidth = pdf.getTextWidth(line);
      pdf.text(line, companyX - lineWidth, companyY);
      companyY += 0.16;
    });
  }

  // "INVOICE" title (top left, large)
  pdf.setFontSize(32);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
  pdf.text('INVOICE', margin, y + 0.3);

  y += 0.5;

  // Invoice number
  if (invoiceNumber) {
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);
    pdf.text(`#${invoiceNumber}`, margin, y);
    y += 0.25;
  }

  // Invoice date and due date
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(80, 80, 80);

  if (invoiceDate) {
    pdf.text(`Date: ${formatDate(invoiceDate)}`, margin, y);
    y += 0.18;
  }
  if (dueDate) {
    pdf.text(`Due Date: ${formatDate(dueDate)}`, margin, y);
    y += 0.18;
  }

  y += 0.3;

  // ============ CLIENT SECTION ============

  if (clientName) {
    // Draw client box
    pdf.setFillColor(248, 249, 250);
    pdf.roundedRect(margin, y, contentWidth, 0.8, 0.05, 0.05, 'F');

    y += 0.15;

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text('BILL TO', margin + 0.15, y + 0.08);

    y += 0.2;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);
    pdf.text(clientName, margin + 0.15, y + 0.05);

    y += 0.2;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(80, 80, 80);

    const clientDetails = [];
    if (clientAddress) clientAddress.split('\n').forEach(line => clientDetails.push(line.trim()));
    if (clientPhone) clientDetails.push(clientPhone);
    if (clientEmail) clientDetails.push(clientEmail);

    clientDetails.slice(0, 3).forEach(line => {
      pdf.text(line, margin + 0.15, y + 0.05);
      y += 0.15;
    });

    y += 0.15;
  }

  y += 0.2;

  // ============ PROJECT NAME ============

  if (invoiceName) {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);
    pdf.text(invoiceName, margin, y);
    y += 0.35;
  }

  // ============ MATERIALS TABLE ============

  if (lineItems.length > 0) {
    checkPageBreak(1);

    // Section header
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.text('MATERIALS', margin, y);

    // Underline
    pdf.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.setLineWidth(0.02);
    pdf.line(margin, y + 0.05, margin + 1.2, y + 0.05);

    y += 0.25;

    // Table header
    const colWidths = {
      item: contentWidth * 0.45,
      qty: contentWidth * 0.12,
      unit: contentWidth * 0.13,
      price: contentWidth * 0.15,
      total: contentWidth * 0.15,
    };

    pdf.setFillColor(0, 102, 204);
    pdf.rect(margin, y, contentWidth, 0.28, 'F');

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);

    let colX = margin + 0.1;
    pdf.text('ITEM', colX, y + 0.18);
    colX += colWidths.item;
    pdf.text('QTY', colX, y + 0.18);
    colX += colWidths.qty;
    pdf.text('UNIT', colX, y + 0.18);
    colX += colWidths.unit;
    pdf.text('PRICE', colX, y + 0.18);
    colX += colWidths.price;
    pdf.text('TOTAL', colX, y + 0.18);

    y += 0.28;

    // Table rows
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(50, 50, 50);

    lineItems.forEach((item, index) => {
      checkPageBreak(0.35);

      // Alternate row background
      if (index % 2 === 0) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(margin, y, contentWidth, 0.3, 'F');
      }

      pdf.setFontSize(9);
      colX = margin + 0.1;

      // Item name (truncate if too long)
      let itemName = item.name || '';
      if (pdf.getTextWidth(itemName) > colWidths.item - 0.2) {
        while (pdf.getTextWidth(itemName + '...') > colWidths.item - 0.2 && itemName.length > 0) {
          itemName = itemName.slice(0, -1);
        }
        itemName += '...';
      }
      pdf.text(itemName, colX, y + 0.18);

      colX += colWidths.item;
      pdf.text(String(item.quantity || 0), colX, y + 0.18);

      colX += colWidths.qty;
      pdf.text(item.unit || '', colX, y + 0.18);

      colX += colWidths.unit;
      pdf.text(formatCurrency(item.unitPrice), colX, y + 0.18);

      colX += colWidths.price;
      const lineTotal = (item.quantity || 0) * (item.unitPrice || 0);
      pdf.text(formatCurrency(lineTotal), colX, y + 0.18);

      y += 0.3;
    });

    // Materials subtotal
    pdf.setFillColor(230, 240, 255);
    pdf.rect(margin, y, contentWidth, 0.28, 'F');

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.text('Materials Subtotal', margin + 0.1, y + 0.18);
    pdf.text(formatCurrency(materialsSubtotal), margin + contentWidth - 0.6, y + 0.18);

    y += 0.45;
  }

  // ============ LABOR TABLE ============

  if (laborItems.length > 0) {
    checkPageBreak(1);

    // Section header
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.text('LABOR', margin, y);

    // Underline
    pdf.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.setLineWidth(0.02);
    pdf.line(margin, y + 0.05, margin + 0.8, y + 0.05);

    y += 0.25;

    // Table header
    pdf.setFillColor(0, 102, 204);
    pdf.rect(margin, y, contentWidth, 0.28, 'F');

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text('DESCRIPTION', margin + 0.1, y + 0.18);
    pdf.text('AMOUNT', margin + contentWidth - 0.6, y + 0.18);

    y += 0.28;

    // Table rows
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(50, 50, 50);

    laborItems.forEach((item, index) => {
      checkPageBreak(0.35);

      if (index % 2 === 0) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(margin, y, contentWidth, 0.3, 'F');
      }

      pdf.setFontSize(9);
      pdf.text(item.description || '', margin + 0.1, y + 0.18);
      pdf.text(formatCurrency(item.amount), margin + contentWidth - 0.6, y + 0.18);

      y += 0.3;
    });

    // Labor subtotal
    pdf.setFillColor(230, 240, 255);
    pdf.rect(margin, y, contentWidth, 0.28, 'F');

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    pdf.text('Labor Subtotal', margin + 0.1, y + 0.18);
    pdf.text(formatCurrency(laborSubtotal), margin + contentWidth - 0.6, y + 0.18);

    y += 0.45;
  }

  // ============ TOTALS SECTION ============

  checkPageBreak(1);

  const totalsX = margin + contentWidth - 2.5;
  const totalsWidth = 2.5;

  // Subtotal
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 60, 60);
  pdf.text('Subtotal', totalsX, y + 0.15);
  pdf.text(formatCurrency(subtotal), totalsX + totalsWidth - 0.1, y + 0.15, { align: 'right' });
  y += 0.25;

  // Tax (if applicable)
  if (taxRate > 0) {
    pdf.text(`Tax (${taxRate}%)`, totalsX, y + 0.15);
    pdf.text(formatCurrency(taxAmount), totalsX + totalsWidth - 0.1, y + 0.15, { align: 'right' });
    y += 0.25;
  }

  // Grand total box
  pdf.setFillColor(0, 102, 204);
  pdf.rect(totalsX - 0.1, y, totalsWidth + 0.2, 0.4, 'F');

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('AMOUNT DUE', totalsX + 0.05, y + 0.26);
  pdf.text(formatCurrency(total), totalsX + totalsWidth - 0.05, y + 0.26, { align: 'right' });

  y += 0.7;

  // ============ PAYMENT TERMS BOX ============

  checkPageBreak(0.8);

  pdf.setFillColor(230, 240, 255);
  pdf.setDrawColor(0, 102, 204);
  pdf.setLineWidth(0.02);
  pdf.roundedRect(margin, y, contentWidth, 0.6, 0.05, 0.05, 'FD');

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 102, 204);
  pdf.text('PAYMENT TERMS', margin + 0.15, y + 0.2);

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(30, 30, 30);
  pdf.text(PAYMENT_TERMS_LABELS[paymentTerms] || 'Net 30', margin + 0.15, y + 0.4);

  if (dueDate) {
    pdf.text(`Due: ${formatDate(dueDate)}`, margin + contentWidth - 0.15, y + 0.4, { align: 'right' });
  }

  y += 0.8;

  // ============ SCOPE OF WORK ============

  if (scopeOfWork) {
    checkPageBreak(1);

    pdf.setFillColor(248, 249, 250);

    // Calculate height needed for scope text
    pdf.setFontSize(9);
    const scopeLines = pdf.splitTextToSize(scopeOfWork, contentWidth - 0.3);
    const scopeHeight = Math.max(0.8, scopeLines.length * 0.16 + 0.4);

    pdf.roundedRect(margin, y, contentWidth, scopeHeight, 0.05, 0.05, 'F');

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(100, 100, 100);
    pdf.text('DESCRIPTION OF WORK', margin + 0.15, y + 0.2);

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);
    pdf.text(scopeLines, margin + 0.15, y + 0.4);

    y += scopeHeight + 0.2;
  }

  // ============ TERMS AND CONDITIONS ============

  if (termsAndConditions) {
    checkPageBreak(1);

    pdf.setFillColor(248, 249, 250);

    pdf.setFontSize(9);
    const termsLines = pdf.splitTextToSize(termsAndConditions, contentWidth - 0.3);
    const termsHeight = Math.max(0.8, termsLines.length * 0.16 + 0.4);

    pdf.roundedRect(margin, y, contentWidth, termsHeight, 0.05, 0.05, 'F');

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(100, 100, 100);
    pdf.text('TERMS & NOTES', margin + 0.15, y + 0.2);

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);
    pdf.text(termsLines, margin + 0.15, y + 0.4);

    y += termsHeight + 0.2;
  }

  // ============ FOOTER ============

  // Draw footer at bottom of page
  const footerY = pageHeight - margin - 0.1;

  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.005);
  pdf.line(margin, footerY - 0.2, pageWidth - margin, footerY - 0.2);

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(120, 120, 120);

  // Thank you message
  pdf.text('Thank you for your business!', margin, footerY);

  // Company contact in footer (if available)
  if (companyProfile?.phone || companyProfile?.email) {
    const contactParts = [];
    if (companyProfile.phone) contactParts.push(companyProfile.phone);
    if (companyProfile.email) contactParts.push(companyProfile.email);
    const contactText = contactParts.join(' | ');
    const contactWidth = pdf.getTextWidth(contactText);
    pdf.text(contactText, pageWidth - margin - contactWidth, footerY);
  }

  // Generate filename
  const safeName = invoiceNumber ? `Invoice_${invoiceNumber}` : (invoiceName || 'Invoice').replace(/[^a-z0-9]/gi, '_');
  const dateStr = new Date().toISOString().slice(0, 10);
  const filename = `${safeName}_${dateStr}.pdf`;

  // Save the PDF
  pdf.save(filename);

  return filename;
}

export default { exportToPDF, exportToPNG, exportToJPEG, exportEstimateToPDF, exportInvoiceToPDF };
